import { AnimatePresence, motion } from 'motion/react'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { useNavigate } from '@lib/react-router-dom'

import { ActionIcon } from '@ui/components/ActionIcon'
import { TourTarget } from '@ui/components/Tourtip/TourTarget'
import { UserIcon } from '@ui/icons/User.icon'

import { Box } from '@shared/components/Box'

import { MenuLink } from '../../MenuLink'
import { MenuSection } from '../../menuSections/useMenuSections'
import { useProfileMenuItems } from '../hooks/useProfileMenuItems'

import styles from './TopBar.module.scss'

export const ProfileMenu: React.FC = () => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const profileItems = useProfileMenuItems()

  const [isOpen, setIsOpen] = useState(false)
  const [languageOpen, setLanguageOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const toggle = useCallback(() => {
    setIsOpen((prev) => !prev)
    setLanguageOpen(false)
  }, [])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
        setLanguageOpen(false)
      }
    }
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (languageOpen) setLanguageOpen(false)
        else setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [languageOpen])

  const resolveIsActive = useCallback(
    (item: MenuSection): boolean => {
      if (item.path && pathname.includes(item.path)) return true
      return !!item.screenChildren?.some((child) => child.path && pathname.includes(child.path))
    },
    [pathname]
  )

  const handleItemClick = useCallback(
    (item: MenuSection) => {
      if (item.id === 'profile-language') {
        setIsOpen(false)
        setLanguageOpen(true)
        return
      }

      if (item.onClick) {
        item.onClick()
      } else if (item.path) {
        navigate(item.path)
      }
      setIsOpen(false)
    },
    [navigate]
  )

  const languageItem = profileItems.find((item) => item.id === 'profile-language')

  const dropdownAnimation = {
    initial: { opacity: 0, y: -8 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -8 },
    transition: { duration: 0.2, ease: 'easeInOut' as const },
  }

  return (
    <TourTarget target='wallets.profile'>
      <div ref={containerRef} className={styles.profileContainer}>
        <Box p='x4' flex align='center' justify='center'>
          <ActionIcon
            icon={{ icon: UserIcon, color: 'text-white' }}
            size='x24'
            onClick={toggle}
            aria-expanded={isOpen || languageOpen}
            aria-label='Profile'
          />
        </Box>

        <AnimatePresence>
          {isOpen && (
            <motion.div className={styles.profileDropdown} {...dropdownAnimation}>
              {profileItems.map((item) => (
                <MenuLink
                  key={item.id}
                  section={{ ...item, screenChildren: undefined }}
                  isActive={resolveIsActive(item)}
                  isVisible
                  isExpanded={false}
                  showChevron={false}
                  onClick={() => handleItemClick(item)}
                  className={styles.profileDropdownItem}
                />
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Language panel */}
        <AnimatePresence>
          {languageOpen && languageItem?.screenChildren && (
            <motion.div className={styles.profileDropdown} {...dropdownAnimation}>
              <div className={styles.profileDropdownDivider} />
              {languageItem.screenChildren.map((child) => (
                <MenuLink
                  key={child.id}
                  section={child}
                  isActive={!!child.isSelected}
                  isVisible
                  isExpanded={false}
                  showChevron={false}
                  onClick={() => {
                    child.onClick?.()
                    setLanguageOpen(false)
                  }}
                  className={styles.profileDropdownItem}
                />
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </TourTarget>
  )
}
